<template>
  <div class="role-table-container">
    <!-- 顶部搜索栏 - Idol选择器 -->
    <div class="search-bar">
      <form class="search-form">
        <div class="form-item">
          <label>Idol:</label>
          <div class="custom-idol-select">
            <div
              class="select-trigger"
              @click="toggleIdolDropdown"
              :class="{ active: idolDropdownVisible }"
            >
              <span class="select-value">{{
                selectedIdolName || "请选择Idol查看角色卡"
              }}</span>
              <i
                class="select-arrow"
                :class="{ 'arrow-up': idolDropdownVisible }"
                >▼</i
              >
            </div>

            <div v-if="idolDropdownVisible" class="select-dropdown">
              <div class="dropdown-container">
                <!-- 左侧团体名称 -->
                <div class="idol-groups">
                  <div
                    v-for="group in idolList"
                    :key="group.groupId"
                    class="group-item"
                    :class="{ active: selectedGroup === group.groupId }"
                    @click="selectGroup(group.groupId)"
                  >
                    {{ group.groupName }}
                  </div>
                </div>

                <!-- 右侧爱豆选项 -->
                <div class="idol-options">
                  <div
                    v-for="idol in currentIdols"
                    :key="idol.idolId"
                    class="idol-option"
                    :class="{ selected: selectedIdolId === idol.idolId }"
                    @click="selectIdol(idol)"
                  >
                    {{ idol.idolName }}
                  </div>

                  <div
                    v-if="!currentIdols || currentIdols.length === 0"
                    class="no-idols"
                  >
                    请先选择左侧团体
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-item">
          <button
            type="button"
            class="btn btn-primary"
            @click="loadRoleCards"
            :disabled="loading || !selectedIdolId"
          >
            {{ loading ? "加载中..." : "查看角色卡" }}
          </button>
        </div>

        <div class="form-item">
          <button
            type="button"
            class="btn btn-success"
            @click="showAddDialog"
            :disabled="!selectedIdolId"
          >
            添加角色卡
          </button>
        </div>
      </form>
    </div>

    <!-- 主体区域 -->
    <div class="main-content" v-if="selectedIdolId">
      <!-- 左侧角色卡列表 -->
      <div class="role-list-panel">
        <div class="panel-header">
          <h3>角色卡列表</h3>
          <div class="role-count">
            共 {{ roleCards ? roleCards.length : 0 }} 个角色卡
          </div>
        </div>

        <div class="role-list" v-if="roleCards && roleCards.length > 0">
          <div
            v-for="card in roleCards"
            :key="card.settingId"
            class="role-item"
            :class="{ active: selectedRoleCard?.settingId === card.settingId }"
            @click="selectRoleCard(card)"
          >
            <div class="role-header">
              <div class="role-id">ID: {{ card.settingId }}</div>
            </div>
            <div class="role-author">创作者: {{ card.author || "未知" }}</div>
            <div class="role-preview">
              {{ getPreviewText(card.settingContent) }}
            </div>
          </div>
        </div>

        <div v-else-if="!loading" class="empty-state">
          <div class="empty-icon">📋</div>
          <div class="empty-text">暂无角色卡</div>
          <div class="empty-hint">为该Idol创建第一个角色卡吧</div>
        </div>

        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <div>加载中...</div>
        </div>
      </div>

      <!-- 右侧角色卡详情 -->
      <div class="role-detail-panel">
        <div v-if="selectedRoleCard" class="role-detail">
          <div class="detail-header">
            <h3>角色卡详情</h3>
            <div class="detail-meta">
              <span>ID: {{ selectedRoleCard.settingId }}</span>
              <span>创作者: {{ selectedRoleCard.author || "未知" }}</span>
            </div>
          </div>

          <div class="detail-content">
            <div class="content-section">
              <div class="section-header">
                <h4>角色设定</h4>
                <button
                  v-if="!editingContent"
                  class="btn btn-edit"
                  @click="startEdit"
                >
                  编辑
                </button>
                <div v-else class="edit-actions">
                  <button
                    class="btn btn-save"
                    @click="saveEdit"
                    :disabled="saving"
                  >
                    {{ saving ? "保存中..." : "保存" }}
                  </button>
                  <button class="btn btn-cancel" @click="cancelEdit">
                    取消
                  </button>
                </div>
              </div>

              <div v-if="!editingContent" class="content-text">
                {{ selectedRoleCard.settingContent }}
              </div>
              <textarea
                v-else
                v-model="editForm.settingContent"
                class="edit-textarea"
                placeholder="请输入详细的角色设定..."
                rows="10"
              ></textarea>
            </div>

            <div class="content-section">
              <h4>开场白</h4>
              <div v-if="!editingContent" class="content-text">
                {{ getStartMessageContent(selectedRoleCard.startMessage) }}
              </div>
              <div v-else>
                <textarea
                  v-model="editForm.messageContent"
                  class="edit-textarea"
                  placeholder="请输入开场白内容..."
                  rows="5"
                ></textarea>
              </div>
            </div>

            <!-- 开场音频区域 -->
            <div class="content-section">
              <h4>开场音频</h4>
              <div
                v-if="selectedRoleCard.startVoice"
                class="start-voice-section"
              >
                <audio
                  :src="selectedRoleCard.startVoice"
                  controls
                  class="start-voice-player"
                  preload="metadata"
                >
                  您的浏览器不支持音频播放。
                </audio>
                <div class="audio-url-info">
                  <small>{{ selectedRoleCard.startVoice }}</small>
                </div>
              </div>
              <div v-else class="no-voice-placeholder">
                <div class="no-voice-icon">🎵</div>
                <div class="no-voice-text">暂无开场音频</div>
                <div class="no-voice-hint">在编辑模式下可以生成音频</div>
              </div>
            </div>

            <!-- 音频生成区域 -->
            <div class="content-section" v-if="editingContent">
              <h4>音频生成</h4>
              <div class="audio-generation-section">
                <div class="form-group">
                  <label>音频内容：</label>
                  <textarea
                    v-model="audioForm.sentence"
                    class="edit-textarea"
                    placeholder="请输入要生成音频的内容..."
                    rows="3"
                  ></textarea>
                </div>

                <div class="form-group-inline">
                  <label>语言选择：</label>
                  <select
                    v-model="audioForm.selectedLanguage"
                    class="language-select"
                  >
                    <option
                      v-for="lang in languages"
                      :key="lang.value"
                      :value="lang.value"
                    >
                      {{ lang.label }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="btn btn-audio"
                    @click="generateStartVoice"
                    :disabled="generatingAudio || !audioForm.sentence.trim()"
                    title="生成音频"
                  >
                    {{ generatingAudio ? "生成中..." : "🎵 生成音频" }}
                  </button>
                </div>

                <!-- 显示当前的startVoice -->
                <div v-if="selectedRoleCard.startVoice" class="current-voice">
                  <label>当前音频：</label>
                  <audio
                    :src="selectedRoleCard.startVoice"
                    controls
                    class="voice-player"
                  ></audio>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="no-selection">
          <div class="no-selection-icon">👈</div>
          <div class="no-selection-text">请从左侧选择一个角色卡查看详情</div>
        </div>
      </div>
    </div>

    <!-- 未选择Idol的提示 -->
    <div v-else class="no-idol-selected">
      <div class="no-idol-icon">🎭</div>
      <div class="no-idol-text">请先选择一个Idol查看其角色卡</div>
    </div>

    <!-- 添加/编辑角色卡对话框 -->
    <div v-if="showDialog" class="dialog-overlay" @click="closeDialog">
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h3>添加角色卡</h3>
          <button class="btn-close" @click="closeDialog">×</button>
        </div>

        <div class="dialog-body">
          <div class="form-group">
            <label>角色设定</label>
            <textarea
              v-model="formData.settingContent"
              class="form-textarea"
              placeholder="请输入详细的角色设定..."
              rows="10"
            ></textarea>
          </div>

          <div class="form-group">
            <label>开场白</label>
            <textarea
              v-model="formData.messageContent"
              class="form-textarea"
              placeholder="请输入开场白内容..."
              rows="5"
            ></textarea>
          </div>
        </div>

        <div class="dialog-footer">
          <button class="btn btn-secondary" @click="closeDialog">取消</button>
          <button
            class="btn btn-primary"
            @click="saveRoleCard"
            :disabled="saving"
          >
            {{ saving ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { idol, chat } from "../../../api/index";

export default {
  name: "RoleTable",
  data() {
    return {
      // Idol相关
      idolList: [],
      selectedGroup: null,
      selectedIdolId: null,
      selectedIdolName: "",
      idolDropdownVisible: false,

      // 角色卡相关
      roleCards: [],
      selectedRoleCard: null,
      loading: false,

      // 右侧编辑相关
      editingContent: false,
      saving: false,
      editForm: {
        settingContent: "",
        messageContent: "",
      },

      // 音频生成相关
      generatingAudio: false,
      audioForm: {
        sentence: "", // 用于生成音频的内容
        selectedLanguage: "Chinese", // 默认选择中文
      },
      languages: [
        { value: "Korean", label: "韩语" },
        { value: "English", label: "英语" },
        { value: "Japanese", label: "日语" },
        { value: "Chinese", label: "中文" },
        { value: "Chinese,Yue", label: "中文（粤语）" },
      ],

      // 对话框相关（仅用于添加新角色卡）
      showDialog: false,
      formData: {
        settingContent: "",
        messageContent: "",
      },
    };
  },

  computed: {
    currentIdols() {
      if (
        !this.selectedGroup ||
        !this.idolList ||
        !Array.isArray(this.idolList)
      )
        return [];
      const group = this.idolList.find((g) => g.groupId === this.selectedGroup);
      return group && group.idols && Array.isArray(group.idols)
        ? group.idols
        : [];
    },
  },

  mounted() {
    this.loadIdolList();
    // 点击外部关闭下拉框
    document.addEventListener("click", this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  methods: {
    // 加载Idol列表
    async loadIdolList() {
      try {
        const res = await idol.getIdolList({});
        console.log("Idol API response:", res); // 调试信息
        if (res.code === 200) {
          this.idolList = res.data || [];
          console.log("IdolList:", this.idolList); // 调试信息
        }
      } catch (error) {
        console.error("加载Idol列表失败:", error);
        this.$message?.error?.("加载Idol列表失败");
      }
    },

    // Idol选择相关方法
    toggleIdolDropdown() {
      this.idolDropdownVisible = !this.idolDropdownVisible;
    },

    selectGroup(groupId) {
      this.selectedGroup = groupId;
    },

    selectIdol(idol) {
      this.selectedIdolId = idol.idolId;
      this.selectedIdolName = idol.idolName;
      this.idolDropdownVisible = false;
      this.roleCards = [];
      this.selectedRoleCard = null;
    },

    handleClickOutside(event) {
      if (!event.target.closest(".custom-idol-select")) {
        this.idolDropdownVisible = false;
      }
    },

    // 角色卡相关方法
    async loadRoleCards() {
      if (!this.selectedIdolId) return;

      this.loading = true;
      try {
        const res = await chat.getCharSetting(this.selectedIdolId);
        if (res.code === 200) {
          this.roleCards = res.data || [];
          this.selectedRoleCard = null;
        } else {
          this.$message?.error?.(res.msg || "加载角色卡失败");
        }
      } catch (error) {
        console.error("加载角色卡失败:", error);
        this.$message?.error?.("加载角色卡失败");
      } finally {
        this.loading = false;
      }
    },

    selectRoleCard(card) {
      this.selectedRoleCard = card;
      this.editingContent = false; // 切换角色卡时退出编辑模式
    },

    getPreviewText(content) {
      if (!content) return "暂无内容";
      return content.length > 100 ? content.substring(0, 100) + "..." : content;
    },

    // 解析开场白JSON格式
    // 解析开场白JSON格式 - 新格式：{"role":"assistant","content":"实际内容"}
    getStartMessageContent(startMessage) {
      if (!startMessage) return "";
      try {
        const parsed = JSON.parse(startMessage);
        return parsed.content || "";
      } catch (error) {
        // 如果不是JSON格式，直接返回原文本（兼容旧数据）
        return startMessage;
      }
    },

    // 生成开场白JSON格式
    buildStartMessageJson(content) {
      return JSON.stringify({
        role: "assistant",
        content: content || "",
      });
    },

    // 右侧编辑方法
    startEdit() {
      if (!this.selectedRoleCard) return;
      this.editingContent = true;
      this.editForm = {
        settingContent: this.selectedRoleCard.settingContent || "",
        messageContent: this.getStartMessageContent(
          this.selectedRoleCard.startMessage
        ),
      };
      // 清空音频表单
      this.audioForm = {
        sentence: "",
        selectedLanguage: "Chinese",
      };
    },

    cancelEdit() {
      this.editingContent = false;
      this.editForm = {
        settingContent: "",
        messageContent: "",
      };
      // 清空音频表单
      this.audioForm = {
        sentence: "",
        selectedLanguage: "Chinese",
      };
    },

    // 生成音频
    async generateStartVoice() {
      if (!this.audioForm.sentence.trim()) {
        this.$message?.warning?.("请先输入音频内容");
        return;
      }

      if (!this.selectedIdolId) {
        this.$message?.warning?.("请先选择Idol");
        return;
      }

      this.generatingAudio = true;

      try {
        const data = {
          sentence: this.audioForm.sentence.trim(),
          language: this.audioForm.selectedLanguage,
          idolId: this.selectedIdolId,
        };

        console.log("生成音频参数:", data);

        const res = await chat.setSentenceAudio(data);

        if (res.code === 200) {
          // 更新当前角色卡的startVoice
          if (res.data) {
            const audioUrl = res.data;
            this.selectedRoleCard.startVoice = audioUrl;

            // 调用update接口将startVoice存入数据库
            await this.updateRoleCardWithVoice(audioUrl);

            this.$message?.success?.("音频生成并保存成功");
          } else {
            this.$message?.warning?.("音频生成成功，但未返回音频地址");
          }
        } else {
          this.$message?.error?.(res.msg || "音频生成失败");
        }
      } catch (error) {
        console.error("生成音频失败:", error);
        this.$message?.error?.("音频生成失败");
      } finally {
        this.generatingAudio = false;
      }
    },

    // 更新角色卡的startVoice到数据库
    async updateRoleCardWithVoice(audioUrl) {
      try {
        const updateData = {
          settingId: this.selectedRoleCard.settingId,
          settingContent: this.selectedRoleCard.settingContent,
          startMessage: this.selectedRoleCard.startMessage,
          startVoice: audioUrl,
        };

        console.log("更新startVoice数据:", updateData);

        const res = await chat.updateCharSetting(updateData);
        if (res.code === 200) {
          console.log("startVoice保存成功");
          // 更新列表中的数据
          const cardIndex = this.roleCards.findIndex(
            (card) => card.settingId === this.selectedRoleCard.settingId
          );
          if (cardIndex !== -1) {
            this.roleCards[cardIndex].startVoice = audioUrl;
          }
        } else {
          this.$message?.warning?.(res.msg || "音频保存到数据库失败");
        }
      } catch (error) {
        console.error("更新startVoice失败:", error);
        this.$message?.warning?.("音频保存到数据库失败");
      }
    },

    async saveEdit() {
      if (!this.selectedRoleCard || !this.editForm.settingContent.trim()) {
        this.$message?.error?.("请输入角色设定");
        return;
      }

      this.saving = true;
      try {
        const startMessageJson = this.buildStartMessageJson(
          this.editForm.messageContent.trim()
        );

        const data = {
          settingId: this.selectedRoleCard.settingId,
          settingContent: this.editForm.settingContent.trim(),
          startMessage: startMessageJson,
        };

        const res = await chat.updateCharSetting(data);
        if (res.code === 200) {
          this.$message?.success?.("保存成功");
          // 更新当前选中的角色卡数据
          this.selectedRoleCard.settingContent =
            this.editForm.settingContent.trim();
          this.selectedRoleCard.startMessage = startMessageJson;
          // 更新列表中的数据
          const cardIndex = this.roleCards.findIndex(
            (card) => card.settingId === this.selectedRoleCard.settingId
          );
          if (cardIndex !== -1) {
            this.roleCards[cardIndex] = { ...this.selectedRoleCard };
          }
          this.editingContent = false;
        } else {
          this.$message?.error?.(res.msg || "保存失败");
        }
      } catch (error) {
        console.error("保存角色卡失败:", error);
        this.$message?.error?.("保存失败");
      } finally {
        this.saving = false;
      }
    },

    // 对话框相关方法（仅用于添加）
    showAddDialog() {
      this.formData = {
        settingContent: "",
        messageContent: "",
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.formData = {
        settingContent: "",
        messageContent: "",
      };
    },

    async saveRoleCard() {
      if (!this.formData.settingContent.trim()) {
        this.$message?.error?.("请输入角色设定");
        return;
      }

      this.saving = true;
      try {
        const startMessageJson = this.buildStartMessageJson(
          this.formData.messageContent.trim()
        );

        const data = {
          idolId: this.selectedIdolId,
          userId: 2, // 默认使用 userId = 2
          settingContent: this.formData.settingContent.trim(),
          startMessage: startMessageJson,
        };

        const res = await chat.addCharSetting(data);
        if (res.code === 200) {
          this.$message?.success?.("添加成功");
          this.closeDialog();
          this.loadRoleCards();
        } else {
          this.$message?.error?.(res.msg || "添加失败");
        }
      } catch (error) {
        console.error("添加角色卡失败:", error);
        this.$message?.error?.("添加失败");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.role-table-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 搜索栏样式 */
.search-bar {
  padding: 20px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
}

.search-form {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.form-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-item label {
  font-weight: bold;
  color: #333;
  white-space: nowrap;
}

/* Idol选择器样式 */
.custom-idol-select {
  position: relative;
  min-width: 250px;
}

.select-trigger {
  padding: 8px 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s;
}

.select-trigger:hover,
.select-trigger.active {
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.select-value {
  color: #333;
}

.select-arrow {
  transition: transform 0.3s;
  color: #666;
}

.select-arrow.arrow-up {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #ffd700;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
}

.dropdown-container {
  display: flex;
  max-height: 300px;
}

.idol-groups {
  width: 120px;
  background: #f8f8f8;
  border-right: 1px solid #eee;
  overflow-y: auto;
}

.group-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
}

.group-item:hover {
  background: #e8e8e8;
}

.group-item.active {
  background: #ffd700;
  font-weight: bold;
}

.idol-options {
  flex: 1;
  overflow-y: auto;
}

.idol-option {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
}

.idol-option:hover {
  background: #f0f0f0;
}

.idol-option.selected {
  background: #fff1c1;
  font-weight: bold;
}

.no-idols {
  padding: 20px;
  text-align: center;
  color: #999;
}

/* 按钮样式 */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #ffd700;
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background: #ffed4a;
  transform: translateY(-1px);
}

.btn-success {
  background: #4caf50;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #45a049;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

/* 主体区域样式 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: row; /* 明确指定水平排列 */
  overflow: hidden;
  height: calc(100vh - 120px); /* 确保有足够高度 */
}

.role-list-panel {
  width: 400px;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  background: #fafafa;
}

.panel-header {
  padding: 20px;
  border-bottom: 1px solid #e8e8e8;
  background: white;
}

.panel-header h3 {
  margin: 0 0 8px 0;
  color: #333;
}

.role-count {
  color: #666;
  font-size: 14px;
}

.role-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.role-item {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.role-item:hover {
  border-color: #ffd700;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.role-item.active {
  border-color: #ffd700;
  background: #fff9e6;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
}

.role-header {
  margin-bottom: 8px;
}

.role-id {
  font-weight: bold;
  color: #333;
}

.role-author {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
}

.role-preview {
  color: #555;
  font-size: 13px;
  line-height: 1.4;
  max-height: 60px;
  overflow: hidden;
}

/* 右侧详情面板 */
.role-detail-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.role-detail {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.detail-header {
  padding: 20px;
  border-bottom: 1px solid #e8e8e8;
  background: white;
}

.detail-header h3 {
  margin: 0 0 8px 0;
  color: #333;
}

.detail-meta {
  display: flex;
  gap: 20px;
  color: #666;
  font-size: 14px;
}

.detail-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.content-section {
  margin-bottom: 30px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.content-section h4 {
  margin: 0;
  color: #333;
  font-size: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #ffd700;
  flex: 1;
}

.edit-actions {
  display: flex;
  gap: 8px;
}

.btn-edit,
.btn-save,
.btn-cancel {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s;
}

.btn-edit {
  background: #ffd700;
  color: #000;
}

.btn-edit:hover {
  background: #ffed4a;
}

.btn-save {
  background: #4caf50;
  color: white;
}

.btn-save:hover:not(:disabled) {
  background: #45a049;
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background: #5a6268;
}

.edit-textarea {
  width: 100%;
  padding: 10px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.edit-textarea:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.edit-input {
  width: 100%;
  padding: 8px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.edit-input:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.content-text {
  line-height: 1.6;
  color: #555;
  white-space: pre-wrap;
  word-wrap: break-word;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #ffd700;
}

/* 空状态样式 */
.empty-state,
.loading-state,
.no-selection,
.no-idol-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #999;
}

.empty-icon,
.no-selection-icon,
.no-idol-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text,
.no-selection-text,
.no-idol-text {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
}

.empty-hint {
  font-size: 14px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ffd700;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.no-idol-selected {
  height: calc(100vh - 120px);
}

/* 对话框样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.dialog-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.dialog-header {
  padding: 20px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
}

.dialog-header h3 {
  margin: 0;
  color: #333;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f0f0f0;
  color: #333;
}

.dialog-body {
  padding: 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 10px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
}

.dialog-footer {
  padding: 20px;
  border-top: 1px solid #e8e8e8;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  background: #fafafa;
}

/* 滚动条样式 */
.role-list::-webkit-scrollbar,
.detail-content::-webkit-scrollbar,
.dialog-body::-webkit-scrollbar,
.idol-groups::-webkit-scrollbar,
.idol-options::-webkit-scrollbar {
  width: 6px;
}

.role-list::-webkit-scrollbar-track,
.detail-content::-webkit-scrollbar-track,
.dialog-body::-webkit-scrollbar-track,
.idol-groups::-webkit-scrollbar-track,
.idol-options::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.role-list::-webkit-scrollbar-thumb,
.detail-content::-webkit-scrollbar-thumb,
.dialog-body::-webkit-scrollbar-thumb,
.idol-groups::-webkit-scrollbar-thumb,
.idol-options::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.role-list::-webkit-scrollbar-thumb:hover,
.detail-content::-webkit-scrollbar-thumb:hover,
.dialog-body::-webkit-scrollbar-thumb:hover,
.idol-groups::-webkit-scrollbar-thumb:hover,
.idol-options::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式样式 */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }

  .role-list-panel {
    width: 100%;
    height: 300px;
  }

  .search-form {
    flex-direction: column;
    align-items: stretch;
  }

  .form-item {
    flex-direction: column;
    align-items: stretch;
  }

  .custom-idol-select {
    min-width: auto;
  }
}

/* 音频生成相关样式 */
.audio-generation-section {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #4caf50;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #333;
}

.form-group-inline {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 15px;
}

.form-group-inline label {
  font-weight: bold;
  color: #333;
  white-space: nowrap;
}

.language-select {
  padding: 6px 10px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s;
}

.language-select:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.btn-audio {
  background: #4caf50;
  color: white;
  padding: 6px 12px;
  font-size: 14px;
  white-space: nowrap;
}

.btn-audio:hover:not(:disabled) {
  background: #45a049;
  transform: translateY(-1px);
}

.current-voice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.current-voice label {
  font-weight: bold;
  color: #333;
  white-space: nowrap;
}

.voice-player {
  height: 32px;
  border-radius: 4px;
  outline: none;
}

/* 开场音频相关样式 */
.start-voice-section {
  padding: 15px;
  background: #f0f8ff;
  border-radius: 8px;
  border-left: 4px solid #1e90ff;
}

.start-voice-player {
  width: 100%;
  height: 40px;
  border-radius: 6px;
  outline: none;
  margin-bottom: 8px;
}

.audio-url-info {
  color: #666;
  word-break: break-all;
  font-family: monospace;
  font-size: 12px;
  padding: 5px 8px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
}

.no-voice-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #ddd;
  color: #999;
  text-align: center;
}

.no-voice-icon {
  font-size: 32px;
  margin-bottom: 10px;
  opacity: 0.6;
}

.no-voice-text {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #666;
}

.no-voice-hint {
  font-size: 14px;
  color: #999;
}
</style>
